

import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    CardBody,
    Col,
    Modal,
    ModalBody,
    ModalHeader,
    Row,
} from "reactstrap";
import TableContainer from "../../components/Common/TableContainer";
import { Column } from "react-table";
import { toast } from "react-toastify";
import AdminCreateShiftModal from "./AdminCreateShiftModal";
import { AdminService } from "services/AdminService";
import { Shift } from "models/Shift";
import Swal from "sweetalert2";

type ViewType = "active" | "upcoming" | "past";

const AdminShiftTableContent: React.FC = () => {
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [refresh, setRefresh] = useState(false);

    // UI controls
    const [viewType, setViewType] = useState<ViewType>("active");
    const [driverModal, setDriverModal] = useState(false);
    const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
    const [shiftToEdit, setShiftToEdit] = useState<Shift | null>(null);

    // pagination for past
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);

    const [deleteModal, setDeleteModal] = useState(false);
    const [shiftToDelete, setShiftToDelete] = useState<Shift | null>(null);


    const toggleModal = () => setModal(!modal);

    const openDriverModal = (shift: Shift) => {
        setSelectedShift(shift);
        setDriverModal(true);
    };
    const openEditModal = (shift: Shift) => {
        setShiftToEdit(shift);
        setEditModal(true);
    };



    const confirmDelete = (shift: Shift) => {
        setShiftToDelete(shift);
        setDeleteModal(true);
    };



    const handleDelete = async (shiftId: string) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This action will permanently delete the shift.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {

                    const res = await AdminService.deleteShift({ _id: shiftId });
                    if (res.success) {
                        Swal.fire({
                            icon: "success",
                            title: "Deleted!",
                            text: "Shift has been deleted successfully.",
                            confirmButtonColor: "#3085d6",
                        });
                        setRefresh(!refresh);
                    } else {
                        Swal.fire({
                            icon: "error",
                            title: "Failed!",
                            text: res.message || "Failed to delete shift.",
                            confirmButtonColor: "#d33",
                        });
                    }
                } catch (err) {
                    console.error("❌ Delete shift error:", err);
                    Swal.fire({
                        icon: "error",
                        title: "Error!",
                        text: "Something went wrong while deleting the shift.",
                        confirmButtonColor: "#d33",
                    });
                }
            }
        });
    };


    const fetchShifts = async () => {
        try {
            setLoading(true);
            const response = await AdminService.getAllShifts();
            if (response.success && Array.isArray(response.data)) {
                setShifts(response.data);
            } else if (response.data) {
                setShifts(response.data);
            } else {
                toast.error("No shift data available", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
                setShifts([]);
            }
        } catch (error) {
            console.error("❌ Failed to fetch shifts:", error);
            toast.error("Error fetching shifts", {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchShifts();
    }, [refresh]);

    // ✅ NEW: Format date without timezone conversion
    const formatDate = (dateString: string) => {
        // Extract date part directly from ISO string (YYYY-MM-DD)
        return dateString.split('T')[0];
    };

    // ✅ NEW: Format time without timezone conversion
    const formatTime = (dateString: string) => {
        // Extract time part from ISO string
        const timePart = dateString.split('T')[1]?.split('.')[0] || '00:00:00';
        const [hours, minutes] = timePart.split(':');
        
        // Convert to 12-hour format
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        
        return `${String(displayHour).padStart(2, '0')}:${minutes} ${ampm}`;
    };

    // ✅ NEW: Parse dates as local time for comparison (without timezone conversion)
    const parseAsLocalDate = (dateString: string): Date => {
        // Remove 'Z' to prevent UTC conversion
        return new Date(dateString.replace('Z', ''));
    };

    // Categorize shifts
    const now = new Date();
    const upcomingShifts = shifts.filter(
        (shift) => parseAsLocalDate(shift.startDateTime) > now
    );
    const activeShifts = shifts.filter(
        (shift) =>
            parseAsLocalDate(shift.startDateTime) <= now && 
            parseAsLocalDate(shift.endDateTime) > now
    );
    const pastShifts = shifts
        .filter((shift) => parseAsLocalDate(shift.endDateTime) <= now)
        .sort(
            (a, b) =>
                parseAsLocalDate(b.endDateTime).getTime() - 
                parseAsLocalDate(a.endDateTime).getTime()
        );

    // Paginate past shifts
    const paginatedPast = pastShifts.slice(0, page * pageSize);
    const canLoadMore = paginatedPast.length < pastShifts.length;

    const getVisibleShifts = () => {
        if (viewType === "active") return activeShifts;
        if (viewType === "upcoming") return upcomingShifts;
        return paginatedPast;
    };

    // Table columns
    const columns: Column<Shift>[] = [
        {
            Header: "Start Date & Time",
            accessor: "startDateTime",
            Cell: ({ value }) => `${formatDate(value)} ${formatTime(value)}`,
        },
        {
            Header: "End Date & Time",
            accessor: "endDateTime",
            Cell: ({ value }) => `${formatDate(value)} ${formatTime(value)}`,
        },
        {
            Header: "Required Drivers",
            accessor: "requiredDrivers",
        },
        {
            Header: "Accepted Drivers",
            accessor: "acceptedDrivers",
            Cell: ({ value }) => value?.length || 0,
        },
        {
            Header: "Status",
            accessor: "status",
            Cell: ({ value }) => (
                <span
                    className={`badge rounded-pill text-uppercase fw-semibold ${
                        value === "open"
                            ? "bg-success"
                            : value === "full"
                            ? "bg-primary"
                            : value === "completed"
                            ? "bg-info"
                            : "bg-secondary"
                    }`}
                >
                    {value}
                </span>
            ),
        },

        {
            Header: "Action",
            id: "action",
            Cell: ({ row }) => (
                <div className="d-flex gap-2">
                    <Button
                        color="info"
                        size="sm"
                        onClick={() => openDriverModal(row.original)}
                    >
                        View
                    </Button>
                    <Button
                        color="warning"
                        size="sm"
                        onClick={() => openEditModal(row.original)}
                    >
                        Edit
                    </Button>
                    <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleDelete(row.original._id)}
                    >
                        Delete
                    </Button>

                </div>
            ),
        },
    ];

    return (
        <Col lg={12}>
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold mb-0">Shift Management</h5>
                        <Button color="primary" size="sm" onClick={toggleModal}>
                            + Create Shift
                        </Button>
                    </div>

                    {/* View type toggle buttons */}
                    <div className="d-flex gap-2 mb-4">
                        <Button
                            color={viewType === "active" ? "primary" : "outline-primary"}
                            size="sm"
                            onClick={() => setViewType("active")}
                        >
                            Active Shifts
                        </Button>
                        <Button
                            color={viewType === "upcoming" ? "primary" : "outline-primary"}
                            size="sm"
                            onClick={() => setViewType("upcoming")}
                        >
                            Upcoming Shifts
                        </Button>
                        <Button
                            color={viewType === "past" ? "primary" : "outline-primary"}
                            size="sm"
                            onClick={() => setViewType("past")}
                        >
                            Past Shifts
                        </Button>
                    </div>

                    {/* Shift Table */}
                    <TableContainer
                        key={`shift-${viewType}-${getVisibleShifts().length}`}
                        columns={columns}
                        data={getVisibleShifts()}
                        isGlobalFilter={false}
                        customPageSize={getVisibleShifts().length || 10}
                        currentPage={undefined}
                        pagelimit={undefined}
                        handlePageChange={undefined}
                        handleSearch={undefined}
                        handleColumnChange={undefined}
                        selectedColumns={undefined}
                        showColumnChange={undefined}
                        ispopup={undefined}
                        isJobListGlobalFilter={undefined}
                        isAddOptions={undefined}
                        isAddUserList={undefined}
                        handleOrderClicks={undefined}
                        handleUserClick={undefined}
                        handleCustomerClick={undefined}
                        isAddCustList={undefined}
                        className={undefined}
                        isPDFGenerate={undefined}
                        isAddressBook={undefined}
                        hancleDeleteRecords={undefined}
                        handleSelect={undefined}
                        selectedID={undefined}
                        hasCheckBox={undefined}
                        handleSendEmail={undefined}
                        footerData={undefined}
                    />

                    {/* Load more button for past shifts */}
                    {viewType === "past" && canLoadMore && (
                        <div className="text-center mt-3">
                            <Button color="secondary" size="sm" onClick={() => setPage(page + 1)}>
                                Load More
                            </Button>
                        </div>
                    )}

                    {!loading && getVisibleShifts().length === 0 && (
                        <p className="text-center text-muted my-5">
                            No {viewType} shifts available.
                        </p>
                    )}
                </CardBody>
            </Card>


            <AdminCreateShiftModal
                isOpen={modal}
                toggle={toggleModal}
                onSuccess={() => setRefresh(!refresh)}

            />

            {/* Edit Modal */}
            {shiftToEdit && (
                <AdminCreateShiftModal
                    isOpen={editModal}
                    toggle={() => setEditModal(false)}
                    onSuccess={() => setRefresh(!refresh)}
                    existingShift={shiftToEdit}
                />
            )}


            <Modal
                isOpen={driverModal}
                toggle={() => setDriverModal(false)}
                size="lg"
            >
                <ModalHeader toggle={() => setDriverModal(false)}>
                    Accepted Drivers
                </ModalHeader>
                <ModalBody>
                    {selectedShift ? (
                        selectedShift.acceptedDrivers.length > 0 ? (
                            <Row>
                                {selectedShift.acceptedDrivers.map((driver) => (
                                    <Col md={6} key={driver._id} className="mb-3">
                                        <div className="border rounded p-3 shadow-sm bg-light">
                                            <h6 className="fw-bold mb-1">
                                                {driver.firstName} {driver.lastName}
                                            </h6>
                                            <p className="mb-0 small text-muted">
                                                {driver.vehicleType
                                                    ? `${driver.vehicleType.toUpperCase()} • `
                                                    : ""}
                                                {driver.phoneNumber || "No phone"}
                                            </p>
                                            {driver.address?.city && (
                                                <p className="mb-0 small">📍 {driver.address.city}</p>
                                            )}
                                            {driver.email && (
                                                <p className="mb-0 small text-muted">{driver.email}</p>
                                            )}
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                            <p className="text-muted text-center mb-0">
                                No drivers have accepted this shift yet.
                            </p>
                        )
                    ) : (
                        <p className="text-center text-muted">No shift selected.</p>
                    )}
                </ModalBody>
            </Modal>
        </Col>
    );
};

export default AdminShiftTableContent;









